import { TextButton } from '@approvalmax/ui';
import { memo, useCallback, useMemo } from 'react';

import {
    accountCurrencyKey,
    bankCountryCodeKey,
    entityTypeKey,
    localClearingSystemKey,
    paymentMethodKey,
} from '../../AddBeneficiaryPopup.constants';
import { messages } from '../../AddBeneficiaryPopup.messages';
import { NavigationButtonsProps } from './NavigationButtons.types';

const NavigationButtons = memo<NavigationButtonsProps>((props) => {
    const {
        activeStep,
        createBenefeciary,
        fieldsSchema,
        formValues,
        isLoadingFieldsSchema,
        companyId,
        validationErrors,
        setFieldsSchemaTransfer,
        setActiveStep,
        setErrorMessages,
    } = props;

    const goToNextStep = useCallback(() => {
        if (activeStep === 1) {
            setFieldsSchemaTransfer({
                companyId: companyId,
                accountCurrency: formValues[accountCurrencyKey],
                bankCountryCode: formValues[bankCountryCodeKey],
                entityType: formValues[entityTypeKey],
            });
        }

        if (activeStep === 2) {
            setFieldsSchemaTransfer({
                companyId: companyId,
                accountCurrency: formValues[accountCurrencyKey],
                bankCountryCode: formValues[bankCountryCodeKey],
                entityType: formValues[entityTypeKey],
                paymentMethod: formValues[paymentMethodKey],
                localClearingSystem:
                    formValues[localClearingSystemKey] &&
                    fieldsSchema.find((field) => field.path === localClearingSystemKey)
                        ? formValues[localClearingSystemKey]
                        : undefined,
            });
        }

        setActiveStep(activeStep + 1);
    }, [activeStep, companyId, fieldsSchema, formValues, setActiveStep, setFieldsSchemaTransfer]);

    const goToPrevStep = useCallback(() => {
        setActiveStep(activeStep - 1);
        setErrorMessages([]);
    }, [activeStep, setActiveStep, setErrorMessages]);

    const isNextStepAvailable = useMemo(() => {
        if (
            activeStep === 1 &&
            formValues[entityTypeKey] &&
            formValues[bankCountryCodeKey] &&
            formValues[accountCurrencyKey]
        ) {
            return true;
        }

        if (
            activeStep === 2 &&
            formValues[entityTypeKey] &&
            formValues[bankCountryCodeKey] &&
            formValues[accountCurrencyKey] &&
            formValues[paymentMethodKey]
        ) {
            const localClearingField = fieldsSchema.find((item) => item.path === localClearingSystemKey);

            if ((localClearingField && formValues[localClearingSystemKey]) || !localClearingField) {
                return true;
            }
        }

        if (activeStep === 3) {
            const requiredFields =
                fieldsSchema.reduce<string[]>((acc, field) => {
                    if (field.required) {
                        acc.push(field.path);
                    }

                    return acc;
                }, []) || [];
            const allRequiredFieldsHasValues = requiredFields.every((path) => formValues[path]);

            const hasValidationErrors = Boolean(Object.keys(validationErrors).length);

            if (allRequiredFieldsHasValues && !hasValidationErrors) {
                return true;
            }
        }

        return false;
    }, [activeStep, fieldsSchema, formValues, validationErrors]);

    const buttons = [
        <TextButton key='back' size='large' execute={goToPrevStep} disabled={isLoadingFieldsSchema}>
            {messages.prevStepButtonText}
        </TextButton>,
        <TextButton
            key='next'
            size='large'
            execute={goToNextStep}
            disabled={isLoadingFieldsSchema || !isNextStepAvailable}
        >
            {messages.nextStepButtonText}
        </TextButton>,
    ];

    if (activeStep === 1) {
        buttons.shift();
    }

    if (activeStep === 4) {
        buttons.pop();

        buttons.push(
            <TextButton key='next' size='large' execute={createBenefeciary}>
                {messages.createButtonText}
            </TextButton>
        );
    }

    return <>{buttons}</>;
});

export default NavigationButtons;
